import {
  MotorFunnelState,
  ReferenceData,
  VehicleFromSearch,
  VehicleManuallyEntered,
  WireCar,
  VehicleSearchType,
} from 'scenes/motor/types';
import {
  getAgreedVehicleValue,
  getBusinessType,
  getCoverLevel,
  getMarketOrAgreedValue,
  getOvernightParkingAddress,
  getRecentInsurer,
  getVehicleModifications,
  getVehicleUnderFinance,
  hasModifications,
  hasPreExistingDamage,
  hasSpecialFeatures,
  isVehicleUsedForBusiness,
} from 'scenes/motor/selectors';

const searchType: {
  [key in VehicleSearchType]: string;
} = {
  [VehicleSearchType.RegoLookup]: 'Rego lookup',
  [VehicleSearchType.ManualEntry]: 'Manual search',
  [VehicleSearchType.Unspecified]: '',
};

const getBusinessTypeDescription = (
  state: MotorFunnelState,
  referenceData: ReferenceData
) => {
  const businessType = getBusinessType(state);
  return (
    referenceData.businessTypes.find((type) => type.value === businessType)
      ?.label ?? businessType
  );
};

const getRecentInsurerName = (
  state: MotorFunnelState,
  referenceData: ReferenceData
) => {
  const insurerCode = getRecentInsurer(state);
  return (
    Object.values(referenceData.recentInsurerOptions)
      .flat()
      .find((insurer) => insurer.value === insurerCode)?.label ?? insurerCode
  );
};

const getCoverLevelDescription = (
  state: MotorFunnelState,
  referenceData: ReferenceData
) =>
  referenceData.coverLevelList.find(
    (level) =>
      level.insurerCode === getRecentInsurer(state) &&
      level.value === getCoverLevel(state)
  )?.label || '';

export const toWireCar = (
  state: MotorFunnelState,
  vehicleSearchType: VehicleSearchType,
  vehicle: VehicleFromSearch | VehicleManuallyEntered,
  referenceData: ReferenceData
): WireCar => {
  const { make, model, bodyType: type, year: yearOfManufacture } = vehicle;
  const { postcode } = getOvernightParkingAddress(state);

  return {
    searchType: searchType[vehicleSearchType],
    make,
    model,
    type,
    yearOfManufacture,
    overnightParkingPostCode: postcode,
    carValuation: getMarketOrAgreedValue(state) || '',
    agreedValue: getAgreedVehicleValue(state),
    financeStatus: getVehicleUnderFinance(state),
    usage: {
      type: isVehicleUsedForBusiness(state) ? 'business' : 'personal',
      business: isVehicleUsedForBusiness(state)
        ? getBusinessTypeDescription(state, referenceData)
        : '',
    },
    prevInsurer: {
      name: getRecentInsurerName(state, referenceData),
      levelOfCoverage: getCoverLevelDescription(state, referenceData),
    },
    carConditions: {
      modifications: hasModifications(state),
      modificationsDetails: getVehicleModifications(state).map(
        (modification) => modification.description
      ),
      specialFeatures: hasSpecialFeatures(state),
      damage: hasPreExistingDamage(state),
    },
  };
};
